import { useEffect, useState, useMemo } from 'react';
import api from '../../services/api';
import { TrendingUp, TrendingDown, Users, Eye, ShoppingBag, BarChart3, Activity, Smartphone, Monitor, Globe, RefreshCw, DollarSign, Target, Download, Search, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const getAuthHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem('adminToken')}` });

// Country code → flag emoji (regional indicator letters)
const flagEmoji = (cc) => {
  if (!cc || cc.length !== 2) return '🌐';
  const A = 0x1F1E6;
  return String.fromCodePoint(A + cc.charCodeAt(0) - 65, A + cc.charCodeAt(1) - 65);
};

// ─────────────────── Reusable widgets ───────────────────
const StatCard = ({ title, value, delta, subtitle, Icon, tone = 'teal' }) => {
  const tones = {
    teal: { bg: 'bg-[#2a9d8f]/10', text: 'text-[#2a9d8f]' },
    blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
    amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
    indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600' },
    rose: { bg: 'bg-rose-100', text: 'text-rose-600' },
    green: { bg: 'bg-green-100', text: 'text-green-600' },
  }[tone];
  const positive = typeof delta === 'number' && delta > 0;
  const negative = typeof delta === 'number' && delta < 0;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-slate-500 font-medium uppercase tracking-wide">{title}</span>
        <div className={`p-1.5 rounded-lg ${tones.bg}`}>
          <Icon className={`w-3.5 h-3.5 ${tones.text}`} />
        </div>
      </div>
      <div className="text-2xl font-bold text-slate-900 tabular-nums">{value ?? '–'}</div>
      <div className="flex items-center gap-1 mt-1">
        {positive && <TrendingUp className="w-3 h-3 text-green-500" />}
        {negative && <TrendingDown className="w-3 h-3 text-rose-500" />}
        <span className={`text-xs ${positive ? 'text-green-600' : negative ? 'text-rose-600' : 'text-slate-400'}`}>
          {subtitle}
        </span>
      </div>
    </div>
  );
};

const BarChart = ({ data, valueKey = 'visitors', xKey = 'day', label, color = '#2a9d8f', height = 220 }) => {
  const max = Math.max(1, ...data.map(d => d[valueKey] || 0));
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-[#2a9d8f]" />
        {label}
      </h3>
      <div style={{ height }} className="flex items-end gap-1 relative">
        {data.map((d, i) => {
          const val = d[valueKey] || 0;
          const heightPx = `${Math.max(2, (val / max) * (height - 24))}px`;
          const x = d[xKey];
          return (
            <div key={`${x}-${i}`} className="flex-1 flex flex-col items-center justify-end h-full group min-w-[12px]">
              <div className="text-[10px] text-slate-600 font-semibold opacity-0 group-hover:opacity-100 transition-opacity mb-1 tabular-nums">{val}</div>
              <div
                className="w-full rounded-t hover:opacity-80 transition-opacity"
                style={{ height: heightPx, backgroundColor: color, minHeight: 2 }}
                title={`${x}: ${val}`}
              />
              <div className="text-[9px] text-slate-400 mt-1 truncate w-full text-center">{x.length > 5 ? x.slice(5) : x}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Donut = ({ data, label, colors = ['#2a9d8f', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'] }) => {
  const total = data.reduce((sum, d) => sum + d.count, 0);
  if (total === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">{label}</h3>
        <p className="text-xs text-slate-400 italic">Inga data ännu</p>
      </div>
    );
  }
  let cumulative = 0;
  const segments = data.map((d, i) => {
    const pct = (d.count / total) * 100;
    const start = cumulative;
    cumulative += pct;
    return { ...d, pct, start, color: colors[i % colors.length] };
  });
  const r = 60;
  const cx = 80;
  const cy = 80;
  const circumference = 2 * Math.PI * r;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h3 className="text-sm font-semibold text-slate-800 mb-3">{label}</h3>
      <div className="flex items-center gap-4">
        <svg width="160" height="160" viewBox="0 0 160 160" className="flex-shrink-0">
          {segments.map((seg) => {
            const offset = circumference - (seg.pct / 100) * circumference;
            const rotate = (seg.start / 100) * 360 - 90;
            return (
              <circle
                key={seg.label || seg.name} cx={cx} cy={cy} r={r}
                fill="none" stroke={seg.color} strokeWidth="22"
                strokeDasharray={`${circumference - offset} ${circumference}`}
                transform={`rotate(${rotate} ${cx} ${cy})`}
              />
            );
          })}
          <text x={cx} y={cy + 4} textAnchor="middle" className="text-2xl font-bold fill-slate-800">{total}</text>
        </svg>
        <div className="flex-1 space-y-2">
          {segments.map((seg) => (
            <div key={seg.label || seg.name} className="flex items-center gap-2 text-xs">
              <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: seg.color }} />
              <span className="flex-1 text-slate-700 capitalize truncate">{seg.label || seg.name}</span>
              <span className="text-slate-500 font-mono">{seg.pct.toFixed(0)}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ListCard = ({ title, items, keyFn, primary, secondary, badge, icon: HeaderIcon, emptyText = 'Inga data ännu' }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
      {HeaderIcon && <HeaderIcon className="w-4 h-4 text-[#2a9d8f]" />}
      {title}
    </h3>
    {items.length === 0 ? (
      <p className="text-xs text-slate-400 italic">{emptyText}</p>
    ) : (
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={keyFn(it, i)} className="flex justify-between items-center text-sm border-b border-slate-100 last:border-0 pb-2 last:pb-0 gap-2">
            <span className="text-slate-700 truncate flex-1 font-medium">{primary(it)}</span>
            {badge && <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{badge(it)}</span>}
            <span className="text-slate-500 font-mono text-xs tabular-nums">{secondary(it)}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const RealtimeBadge = ({ visitors, views }) => (
  <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-3 py-1.5">
    <span className="relative flex h-2 w-2">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
    </span>
    <span className="text-xs font-semibold text-green-700">
      {visitors} aktiva nu • {views} visn. senaste 5 min
    </span>
  </div>
);

// ─────────────────── Main page ───────────────────
const RANGE_OPTIONS = [
  { value: 7, label: '7 dagar' },
  { value: 14, label: '14 dagar' },
  { value: 30, label: '30 dagar' },
  { value: 90, label: '90 dagar' },
];

const AdminAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [days, setDays] = useState(7);
  const [exporting, setExporting] = useState(false);

  const fetchData = async (rangeDays = days) => {
    setRefreshing(true);
    try {
      const res = await api.get('/admin/analytics/summary', {
        headers: getAuthHeaders(),
        params: { days: rangeDays },
      });
      setData(res.data);
    } catch {
      toast.error('Kunde inte hämta analysdata');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData(days);
    const intervalId = setInterval(() => fetchData(days), 30000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/analytics/export.csv', {
        headers: getAuthHeaders(),
        params: { days },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `printsout-analytics-${days}d.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exporterade besöksdata (${days} dagar)`);
    } catch {
      toast.error('Kunde inte exportera CSV');
    } finally {
      setExporting(false);
    }
  };

  const safe = useMemo(() => ({
    realtime_visitors: 0, realtime_views: 0,
    today_visitors: 0, today_views: 0,
    yesterday_visitors: 0, yesterday_views: 0,
    week_visitors: 0, week_views: 0,
    month_visitors: 0, month_views: 0,
    window_days: days,
    daily_chart: [], hourly_chart: [],
    top_pages: [], top_referrers: [],
    top_countries: [], top_keywords: [],
    devices: {}, browsers: [],
    orders_today: 0, orders_week: 0, orders_month: 0,
    revenue_today: 0, revenue_week: 0,
    conversion_today: 0, conversion_week: 0,
    ...(data || {}),
  }), [data, days]);

  const trendVisitors = safe.today_visitors - safe.yesterday_visitors;
  const trendViews = safe.today_views - safe.yesterday_views;

  const deviceData = useMemo(() => {
    const map = safe.devices || {};
    return Object.entries(map).map(([name, count]) => ({
      name, count, label: name === 'mobile' ? 'Mobil' : name === 'tablet' ? 'Surfplatta' : 'Dator',
    }));
  }, [safe.devices]);

  const pageLabel = (path) => {
    const map = {
      '/': 'Startsida',
      '/produkter': 'Produkter',
      '/varukorg': 'Varukorg',
      '/kassa': 'Kassa',
      '/konto': 'Mitt konto',
      '/logga-in': 'Logga in',
      '/registrera': 'Registrera',
      '/foretag': 'Företag',
    };
    if (map[path]) return map[path];
    if (path.startsWith('/admin')) return path;
    if (path.startsWith('/produkt/')) return 'Produktsida';
    if (path.startsWith('/kalender')) return 'Kalender-editor';
    if (path.startsWith('/namnskylt')) return 'Namnskylt-editor';
    if (path.startsWith('/fotoalbum')) return 'Fotoalbum-editor';
    if (path.startsWith('/design')) return 'Design-editor';
    return path;
  };

  if (loading) return <div className="p-6 text-sm text-slate-400">Laddar analys...</div>;

  const rangeLabel = RANGE_OPTIONS.find(o => o.value === days)?.label || `${days} dagar`;

  return (
    <div className="space-y-5 max-w-7xl" data-testid="admin-analytics">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-slate-900">Analys</h1>
            <RealtimeBadge visitors={safe.realtime_visitors} views={safe.realtime_views} />
          </div>
          <p className="text-slate-500 text-sm mt-1">Live-statistik från din egen databas — uppdateras var 30:e sekund</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Date range selector */}
          <div className="flex items-center bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="analytics-range-selector">
            {RANGE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setDays(opt.value)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  days === opt.value
                    ? 'bg-[#2a9d8f] text-white'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
                data-testid={`analytics-range-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            onClick={handleExportCsv}
            disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[#2a9d8f] rounded-lg hover:bg-[#268b7e] disabled:opacity-50"
            data-testid="analytics-export-csv-btn"
          >
            <Download className={`w-3.5 h-3.5 ${exporting ? 'animate-pulse' : ''}`} />
            {exporting ? 'Exporterar...' : 'Exportera CSV'}
          </button>
          <button
            onClick={() => fetchData(days)}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
            data-testid="analytics-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Uppdatera
          </button>
        </div>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard title="Besökare idag" value={safe.today_visitors} delta={trendVisitors}
          subtitle={trendVisitors === 0 ? 'Samma som igår' : `${trendVisitors > 0 ? '+' : ''}${trendVisitors} mot igår`}
          Icon={Users} tone="teal" />
        <StatCard title="Sidvisningar" value={safe.today_views} delta={trendViews}
          subtitle={`${safe.week_views.toLocaleString('sv-SE')} senaste ${rangeLabel.toLowerCase()}`}
          Icon={Eye} tone="blue" />
        <StatCard title="Order idag" value={safe.orders_today}
          subtitle={`${safe.orders_week} senaste ${rangeLabel.toLowerCase()}`}
          Icon={ShoppingBag} tone="indigo" />
        <StatCard title="Omsättning idag" value={`${safe.revenue_today.toLocaleString('sv-SE')} kr`}
          subtitle={`${safe.revenue_week.toLocaleString('sv-SE')} kr senaste ${rangeLabel.toLowerCase()}`}
          Icon={DollarSign} tone="green" />
      </div>

      {/* Conversion + period stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard title="Konvertering idag" value={`${safe.conversion_today}%`}
          subtitle={`${safe.conversion_week}% senaste ${rangeLabel.toLowerCase()}`}
          Icon={Target} tone="rose" />
        <StatCard title={`Besökare ${rangeLabel.toLowerCase()}`} value={safe.week_visitors}
          subtitle={`${safe.month_visitors} senaste 30 dagar`}
          Icon={TrendingUp} tone="amber" />
        <StatCard title="Order 30 dagar" value={safe.orders_month}
          subtitle="Totalt"
          Icon={ShoppingBag} tone="indigo" />
        <StatCard title="Aktiva nu" value={safe.realtime_visitors}
          subtitle={`${safe.realtime_views} visn. 5 min`}
          Icon={Activity} tone="green" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <BarChart data={safe.daily_chart} valueKey="visitors" xKey="day"
          label={`Unika besökare per dag (${rangeLabel})`} color="#2a9d8f" />
        <BarChart data={safe.hourly_chart} valueKey="views" xKey="hour"
          label="Sidvisningar senaste 24 timmar" color="#3b82f6" />
      </div>

      {/* Top pages + referrers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <ListCard
          title={`Populäraste sidor (${rangeLabel})`}
          items={safe.top_pages || []}
          keyFn={(it, i) => `${it.path}-${i}`}
          primary={(it) => pageLabel(it.path)}
          secondary={(it) => `${it.views} visn.`}
          badge={(it) => `${it.uniques || 0} unika`}
          icon={BarChart3}
        />
        <ListCard
          title="Trafikkällor (30 dagar)"
          items={safe.top_referrers || []}
          keyFn={(it, i) => `${it.source}-${i}`}
          primary={(it) => (
            <span className="flex items-center gap-1.5">
              <Globe className="w-3 h-3 text-slate-400" />
              {it.source || 'Direkt'}
            </span>
          )}
          secondary={(it) => `${it.visits} besök`}
          icon={Globe}
        />
      </div>

      {/* Countries + Keywords (NEW) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <ListCard
          title={`Länder (${rangeLabel})`}
          items={safe.top_countries || []}
          keyFn={(it, i) => `${it.code}-${i}`}
          primary={(it) => (
            <span className="flex items-center gap-2">
              <span className="text-base leading-none">{flagEmoji(it.code)}</span>
              <span>{it.name}</span>
              <span className="text-[10px] text-slate-400 font-mono">{it.code}</span>
            </span>
          )}
          secondary={(it) => `${it.visits} visn.`}
          badge={(it) => `${it.uniques || 0} unika`}
          icon={MapPin}
          emptyText="Inga geografiska data — kräver att proxyn (Vercel/Cloudflare) skickar geo-headers"
        />
        <ListCard
          title={`Sökord från Google/Bing (${rangeLabel})`}
          items={safe.top_keywords || []}
          keyFn={(it, i) => `${it.keyword}-${i}`}
          primary={(it) => (
            <span className="flex items-center gap-2">
              <Search className="w-3 h-3 text-slate-400 flex-shrink-0" />
              <span className="italic">"{it.keyword}"</span>
            </span>
          )}
          secondary={(it) => `${it.visits} besök`}
          icon={Search}
          emptyText="Sökmotorer skickar oftast inte sökord längre — fångas endast när det finns i referrer-URL"
        />
      </div>

      {/* Devices + browsers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Donut data={deviceData} label={`Enheter (${rangeLabel})`} colors={['#2a9d8f', '#3b82f6', '#f59e0b']} />
        <ListCard
          title={`Webbläsare (${rangeLabel})`}
          items={(safe.browsers || []).slice(0, 6)}
          keyFn={(it) => it.name}
          primary={(it) => (
            <span className="flex items-center gap-1.5">
              {it.name === 'Chrome' || it.name === 'Edge' ? <Monitor className="w-3 h-3 text-slate-400" /> : <Smartphone className="w-3 h-3 text-slate-400" />}
              {it.name}
            </span>
          )}
          secondary={(it) => `${it.count} visn.`}
        />
      </div>
    </div>
  );
};

export default AdminAnalytics;
